import { useEffect, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  type MenuRenderFn,
  type TriggerFn
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { $getRoot, $insertNodes, TextNode } from 'lexical'
import { createPortal } from 'react-dom'
import { systemContractVariables } from '@super-mega/server/contracts/contract'
import { cn } from '@/utils/index.ts'
import { $createContractVariableNode, type ContractVariableOption } from './contract-variable-node.ts'
import { $transformContractVariables } from './utils/variable-transform.ts'

class VariableOption extends MenuOption {
  constructor(public variable: ContractVariableOption & { aliases: readonly string[] }) {
    super(variable.key)
  }
}

const variableOptions = systemContractVariables.map((variable) => new VariableOption(variable))

// 允许紧接中文正文触发，英文 key 中的下划线与点号可用于筛选。
const matchVariableTrigger: TriggerFn = (text) => {
  const match = /@([^@\s{}]*)$/.exec(text)
  return match ? { leadOffset: match.index, matchingString: match[1], replaceableString: match[0] } : null
}

const renderVariableMenu: MenuRenderFn<VariableOption> = (
  anchorRef,
  { options, selectedIndex, setHighlightedIndex, selectOptionAndCleanUp }
) => {
  const anchor = anchorRef.current
  if (!anchor) {
    return null
  }
  return createPortal(
    <div className="border-border bg-surface w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border shadow-lg">
      <div className="border-border text-content-muted border-b px-3 py-2 text-xs">
        插入动态字段 · 可搜索中文名称或字段名
      </div>
      <ul
        role="listbox"
        aria-label="合同动态字段"
        className="m-0 max-h-[min(15rem,35dvh)] list-none overflow-y-auto p-1.5"
      >
        {options.length === 0 ? <li className="text-content-muted px-3 py-2 text-sm">没有匹配的动态字段</li> : null}
        {options.map((option, index) => (
          <li key={option.key} role="presentation">
            <button
              type="button"
              role="option"
              id={`typeahead-item-${index}`}
              aria-selected={selectedIndex === index}
              tabIndex={-1}
              ref={option.setRefElement}
              className={cn(
                'flex w-full flex-col rounded-lg px-3 py-2 text-left text-sm',
                selectedIndex === index ? 'text-accent bg-[var(--accent-soft)]' : 'text-content hover:bg-surface-muted'
              )}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => selectOptionAndCleanUp(option)}
            >
              <span>{option.variable.label}</span>
              <span className="text-content-soft font-mono text-xs">{`{{${option.key}}}`}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="border-border text-content-soft border-t px-3 py-1.5 text-xs">
        ↑↓ 选择 · Enter 插入 · Esc 关闭
      </div>
    </div>,
    anchor
  )
}

export function ContractVariablePlugin({ readOnly }: { readOnly: boolean }) {
  const [editor] = useLexicalComposerContext()
  const [query, setQuery] = useState<string | null>(null)
  useEffect(() => {
    const unregister = editor.registerNodeTransform(TextNode, $transformContractVariables)
    // 初始化时也处理从旧模板加载的普通占位符。
    editor.update(() =>
      $getRoot()
        .getAllTextNodes()
        .forEach((node) => node.markDirty())
    )
    return unregister
  }, [editor])
  if (readOnly) {
    return null
  }
  const search = query?.toLocaleLowerCase() ?? ''
  return (
    <LexicalTypeaheadMenuPlugin<VariableOption>
      options={variableOptions.filter(({ variable }) =>
        [variable.key, variable.label, ...variable.aliases].some((value) => value.toLocaleLowerCase().includes(search))
      )}
      triggerFn={matchVariableTrigger}
      onQueryChange={setQuery}
      menuRenderFn={renderVariableMenu}
      onSelectOption={(option, nodeToRemove, closeMenu) => {
        editor.update(() => {
          const variable = $createContractVariableNode(option.variable)
          if (nodeToRemove) {
            variable.setFormat(nodeToRemove.getFormat()).setStyle(nodeToRemove.getStyle())
            nodeToRemove.replace(variable)
            variable.selectEnd()
          } else {
            $insertNodes([variable])
          }
          closeMenu()
        })
      }}
    />
  )
}
